import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "../Context/AuthContext";
import { Base_URL } from "../url";
import { useNavigate } from "react-router-dom";
// CreditNoteHeader
// Improvements:
// - Uses a single `formData` object to store form values
// - Adds Company Code dropdown fetched from POST /CompanyCode/GetByOrganisationId
//   (sends { organisationId: user?.organisationId } in the body)
// - Better Tailwind-based styling and responsive grid layout
// - Defensive null checks for customer fields when building payload

export default function CreditNoteHeader({ onSubmitted }) {
  const { user } = useAuth();
const navigate =useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);

  const [customer, setCustomer] = useState(null);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [loadingCompanyCodes, setLoadingCompanyCodes] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverResponse, setServerResponse] = useState(null);

  // single form state
  const [formData, setFormData] = useState({
    invoiceNumber: "",
    creditNoteDate: (() => {
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    })(),
    companyCode: "",
  });

  // Fetch invoice numbers
  useEffect(() => {
    let mounted = true;
    async function fetchInvoices() {
      setLoadingInvoices(true);
      setError(null);
      try {
        const res = await axios.get(`${Base_URL}/CreditNote/getInvoices/${user?.organisationId}`);
        const data = res.data || [];
        const normalized = data.map((x) => {
          if (typeof x === "string") return x;
          if (x && (x.invoiceNumber || x.invoiceNo || x.invoicenumber)) {
            return x.invoiceNumber || x.invoiceNo || x.invoicenumber;
          }
          return String(x);
        });
        if (mounted) setInvoices(normalized);
      } catch (err) {
        console.error(err);
        if (mounted) setError("Failed to load invoices");
      } finally {
        if (mounted) setLoadingInvoices(false);
      }
    }
    fetchInvoices();
    return () => (mounted = false);
  }, []);

  // Fetch customer when invoice changes
  useEffect(() => {
    const selected = formData.invoiceNumber;
    if (!selected) {
      setCustomer(null);
      return;
    }
    let mounted = true;
    async function fetchCustomer() {
      setLoadingCustomer(true);
      setError(null);
      try {
        const res = await axios.get(`${Base_URL}/CreditNote/id?id=${encodeURIComponent(selected)}`);
        const data = res.data || null;
        const cust = data?.customer || data?.customerInfo || data?.customerDetails || data;
        if (mounted) setCustomer(cust);
      } catch (err) {
        console.error(err);
        if (mounted) setError("Failed to load customer for selected invoice");
        if (mounted) setCustomer(null);
      } finally {
        if (mounted) setLoadingCustomer(false);
      }
    }
    fetchCustomer();
    return () => (mounted = false);
  }, [formData.invoiceNumber]);

  // Fetch company codes for the organisation
  useEffect(() => {
    let mounted = true;
    async function fetchCompanyCodes() {
      if (!user?.organisationId) return;
      setLoadingCompanyCodes(true);
      setError(null);
      try {
        const res = await axios.post(`${Base_URL}/CompanyCode/GetByOrganisationId`, {
          organisationId: user.organisationId,
        });
        const data = res.data || [];
        // Expecting array of objects like { companyCode: 'C001', description: '...' }
        const normalized = data.map((c) => {
          if (typeof c === "string") return { companyCode: c, description: "" };
          return {
            companyCode: c.companyCode || c.code || c.CompanyCode || "",
            name: c.name || c.companyName || "",
          };
        });
        if (mounted) setCompanyCodes(normalized);
      } catch (err) {
        console.error(err);
        if (mounted) setError("Failed to load company codes");
      } finally {
        if (mounted) setLoadingCompanyCodes(false);
      }
    }
    fetchCompanyCodes();
    return () => (mounted = false);
  }, [user?.organisationId]);

  const payload = useMemo(() => {
    return {
      invoiceNumber: formData.invoiceNumber || null,
      companyCode: formData.companyCode || null,
      customerCode: customer?.customerCode ?? customer?.customerId ?? customer?.id ?? null,
      customerName: customer?.customerName ?? customer?.name ?? null,
      creditNoteDate:new Date(formData.creditNoteDate)|| null,
      organisationId: user?.organisationId ?? null,
      userId: user?.userId ?? null,
      notes:formData.notes
    };
  }, [formData, customer, user]);

  async function handleSubmit(e) {
    e?.preventDefault();
    setSubmitting(true);
    setError(null);
    setServerResponse(null);
    try {
      // Replace with real endpoint if different
      const res = await axios.post(`${Base_URL}/CreditNote`, payload);
      console.log(res.data);
      if(res.status===200)
      {
        navigate(`/creditNote/${res.data?.newid}`)
      }
      setServerResponse(res.data);
      if (typeof onSubmitted === "function") onSubmitted(res.data);
    } catch (err) {
      console.error(err);
      setError("Failed to submit credit note");
    } finally {
      setSubmitting(false);
    }
  }
  console.log(companyCodes)
  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-2xl">
      <h2 className="text-2xl font-semibold mb-4">Create Credit Note</h2>
      <div className="ml-auto text-lg text-gray-500" >Organisation: {user?.organisationId ?? "-"} • User: {user?.userId ?? "-"}</div>
      <br></br>
      {error && <div className="text-red-600 mb-3">{error}</div>}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="col-span-1 md:col-span-1">
          <label className="block text-sm font-medium mb-1">Select Invoice Number</label>
          {loadingInvoices ? (
            <div className="text-sm">Loading invoices...</div>
          ) : (
            <select
              className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
              style={{minWidth:"300px",maxWidth:"300px",minHeight:"40px",fontSize:"12px"} }
              value={formData.invoiceNumber}
              onChange={(e) => setFormData((s) => ({ ...s, invoiceNumber: e.target.value }))}
            >
              <option value="">-- Select invoice --</option>
              {invoices.map((inv) => (
                <option key={inv} value={inv}>
                  {inv}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Company Code</label>
          {loadingCompanyCodes ? (
            <div className="text-sm">Loading company codes...</div>
          ) : (
            <select
              className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
              style={{minWidth:"300px",maxWidth:"300px",minHeight:"40px",fontSize:"12px"} }
              value={formData.companyCode}
              onChange={(e) => setFormData((s) => ({ ...s, companyCode: e.target.value }))}
            >
              <option value="">-- Select company code --</option>
              {companyCodes.map((c) => (
                <option key={c.companyCode} value={c.companyCode}>
                  {c.companyCode}- {c.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Customer</label>
          {loadingCustomer ? (
            <div className="text-sm">Loading customer...</div>
          ) : customer ? (
            typeof customer === "string" ? (
              <div className="p-2 border rounded">{customer}</div>
            ) : (
              <div className="p-3 border rounded bg-gray-50">
                <div className="font-medium" style={{fontSize:"20px",color:"red"} }>{customer.customerName || customer.name || customer.customer || "Unnamed"}</div>
                <div className="text-sm text-gray-600">Code: {customer.customerCode || customer.customerId || customer.id || "-"}</div>
                {customer.contact && <div className="text-sm text-gray-600">Contact: {customer.contact}</div>}
              </div>
            )
          ) : (
            <div className="text-sm text-gray-500">No customer loaded. Select an invoice.</div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Credit Note Date</label>
          <input
            type="date"
            className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
            style={{minWidth:"300px",maxWidth:"300px",minHeight:"40px",fontSize:"12px"} }
            value={formData.creditNoteDate}
            onChange={(e) => setFormData((s) => ({ ...s, creditNoteDate: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Remarks</label>
          <input
            type="text"
            className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
            style={{minWidth:"600px",maxWidth:"600px",minHeight:"50px",fontSize:"12px"} }
            value={formData.notes}
            onChange={(e) => setFormData((s) => ({ ...s, notes: e.target.value }))}
          />
        </div>

        <div className="md:col-span-2 flex items-center gap-3 mt-2">
          <button
            type="submit"
            disabled={!formData.invoiceNumber || !customer || !formData.companyCode || submitting}
            className={`px-5 py-2 rounded-2xl shadow ${!formData.invoiceNumber || !customer || !formData.companyCode || submitting ? "bg-gray-300 cursor-not-allowed" : "bg-blue-600 text-white"}`}
          >
            {submitting ? "Submitting..." : "Create Credit Note"}
          </button>

          {/* <button
            type="button"
            onClick={() => {
              console.log("Credit Note payload:", payload);
              alert("Payload copied to console. Open devtools to inspect.");
            }}
            className="px-4 py-2 rounded-2xl border"
          >
            Preview Payload
          </button> */}

          
        </div>
      </form>
    </div>
  );
}
